import './FacilityImageUpload.scss';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { getClient } from 'src/module/authentication/selector';
import { updateClientAvatar, deleteClientAvatar } from 'src/module/authentication/action';
import { getClientAvatar } from 'src/service/api';
import toast from 'src/utils/toast';
import variables from 'src/style/variable/variables.module.scss';

export default function FacilityImageUpload () {
  const dispatch = useDispatch();
  const client = useSelector(getClient);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const clientId = client.get('id');
  const avatarId = client.get('avatar_id');
  const avatarUrl = client.get('avatar_url');

  // Load existing avatar
  useEffect(() => {
    const loadAvatar = async () => {
      if (avatarId && !avatarUrl && clientId) {
        try {
          const action = { currentClientId: clientId, clientId: clientId };
          const blobUrl = await getClientAvatar(action, clientId);
          setPreviewUrl(blobUrl);
        } catch (error) {
          toast.error('Failed to load existing avatar');
        }
      } else if (avatarUrl) {
        setPreviewUrl(avatarUrl);
      }
    };

    loadAvatar();
  }, [avatarId, avatarUrl, clientId]);

  const handleFileUpload = async (file: File) => {
    // Validate file type - only PNG and SVG
    const allowedTypes = ['image/png', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a PNG or SVG image file');
      return;
    }

    // Create preview URL
    const fileUrl = URL.createObjectURL(file);
    setPreviewUrl(fileUrl);

    // Upload the file
    setIsUploading(true);
    try {
      dispatch(updateClientAvatar(file));
    } catch (error) {
      toast.error('Failed to upload image');
      setPreviewUrl(avatarUrl || null); // Revert to previous image
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
    maxSize: 5 * 1024 * 1024, // 5MB
    multiple: false,
    onDrop: async (acceptedFiles: File[], fileRejections) => {
      if (fileRejections.length > 0) {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === 'file-too-large') {
          toast.error('Image file is too large. Please select an image smaller than 5MB.');
        } else if (error?.code === 'file-invalid-type') {
          toast.error('Please select a PNG or SVG image file.');
        } else {
          toast.error('Invalid file selected');
        }
        return;
      }

      if (acceptedFiles.length === 0) {
        toast.error('No valid image file selected');
        return;
      }

      const file = acceptedFiles[0];
      await handleFileUpload(file);
    }
  });

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleRemoveImage = () => {
    try {
      dispatch(deleteClientAvatar());
      setPreviewUrl(null);
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const dropzoneStyles = {
    border: '2px dashed',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'border-color 0.3s ease',
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isDragActive ? 'action.hover' : 'transparent',
    borderColor: isDragReject ? 'error.main' : isDragAccept ? 'success.main' : variables.greyLight
  };

  return (
    <div className="facility-image-upload" style={{ padding: '16px' }}>
      <Typography
        variant="body1"
        component="label"
        sx={{
          display: 'block',
          lineHeight: 1.43,
          textTransform: 'uppercase',
          marginBottom: '8px',
          fontSize: '12px',
          fontWeight: 600,
          color: 'text.secondary',
        }}
      >
        Facility Image
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ fontSize: '12px' }}>
        Upload a banner image or logo to represent your facility. This will be displayed in the app header.
      </Typography>

      <Box sx={{ mt: 1 }}>
        {previewUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={previewUrl}
              alt="Facility preview"
              sx={{
                width: '100%',
                maxWidth: 480,
                maxHeight: 220,
                objectFit: 'cover',
                border: `2px solid ${variables.greyLight}`,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                padding: '10px'
              }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUploadIcon />}
                component="label"
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Change Image'}
                <input
                  type="file"
                  accept=".png,.svg,image/png,image/svg+xml"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveImage}
                disabled={isUploading}
              >
                Remove
              </Button>
            </Box>
          </Box>
        ) : (
          <div {...getRootProps()} style={dropzoneStyles}>
            <input {...getInputProps()} id="facility-image-input" />
            <CloudUploadIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body1" color="textSecondary">
              {isDragActive
                ? 'Drop the image here...'
                : 'Drag & drop an image here, or click to select'}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Supports PNG, SVG (max 5MB)
            </Typography>
          </div>
        )}
      </Box>
    </div>
  );
}
